/**
 * Suspension analysis from telemetry (fork, shock, accel_z).
 * Aligned with documentation/suspension_metrics_spec.md and suspension events list.
 */

const DEFAULT_MAX_TRAVEL_FRONT_MM = 200;
const DEFAULT_MAX_TRAVEL_REAR_MM = 210;
/** Raw displacement full scale (e.g. 12-bit ADC 0–4095). Used to convert raw counts to mm so max travel reflects actual data. */
const DISP_FULL_SCALE = 4095;
const BOTTOM_OUT_PERCENT = 95;
const BOTTOM_OUT_MIN_MS = 10;
const HIGH_SPEED_VELOCITY_MM_S = 500;
const IMPACT_ACCEL_G = 3;
const HARSH_VELOCITY_MM_S = 300;
const HARSH_ACCEL_G = 2;
const SUSTAINED_VELOCITY_MM_S = 200;
const SUSTAINED_MIN_MS = 50;
const REBOUND_SPIKE_MM_S = -400;
const G_OUT_ACCEL_G = 2.5;
const CHATTER_ZERO_CROSSINGS = 4;
const CHATTER_WINDOW_MS = 100;
const TOP_OUT_PERCENT = 5;
const FILTER_WINDOW = 7;

function getValidNumber(v) {
  const n = typeof v === 'string' ? Number(v) : v;
  return typeof n === 'number' && Number.isFinite(n) ? n : null;
}

function getFiniteMinMax(values) {
  let min = Infinity;
  let max = -Infinity;
  for (let i = 0; i < values.length; i += 1) {
    const n = getValidNumber(values[i]);
    if (n == null) continue;
    if (n < min) min = n;
    if (n > max) max = n;
  }
  if (min === Infinity || max === -Infinity) return null;
  return { min, max };
}

function getFiniteMax(values) {
  const range = getFiniteMinMax(values);
  return range ? range.max : null;
}

function getFiniteMin(values) {
  const range = getFiniteMinMax(values);
  return range ? range.min : null;
}

/** Infer travel in mm. Values in (0,1] = normalized → mm; in (1,2] = meters; else mm. */
function toTravelMm(value, defaultMaxMm) {
  const v = getValidNumber(value);
  if (v === null) return null;
  if (v > 0 && v <= 1) return (v * defaultMaxMm);
  if (v > 1 && v < 2) return v * 1000;
  return v;
}

/** Convert raw displacement (counts 0..DISP_FULL_SCALE) to mm using total travel. */
function rawDispToMm(raw, totalTravelMm) {
  const v = getValidNumber(raw);
  if (v === null || v < 0 || v > DISP_FULL_SCALE) return null;
  return (v / DISP_FULL_SCALE) * totalTravelMm;
}

/** True if value looks like raw displacement counts (not normalized 0–1, not already mm). */
function looksLikeRawDisp(value) {
  const v = getValidNumber(value);
  return v != null && v > 1 && v <= DISP_FULL_SCALE;
}

/** Moving average filter, window size FILTER_WINDOW. */
function movingAverage(arr, window = FILTER_WINDOW) {
  if (!arr.length) return [];
  const half = Math.floor(window / 2);
  const out = [];
  for (let i = 0; i < arr.length; i++) {
    let sum = 0; let count = 0;
    for (let j = Math.max(0, i - half); j <= Math.min(arr.length - 1, i + half); j++) {
      if (arr[j] != null) { sum += arr[j]; count++; }
    }
    out.push(count ? sum / count : null);
  }
  return out;
}

/**
 * Build time-ordered series with travel (mm), velocity (mm/s), and accel_z (g).
 * Telemetry rows: { time, fork, shock, fork_normalized, shock_normalized, accel_x, accel_y, accel_z }.
 */
export function buildSuspensionSeries(telemetryData) {
  if (!telemetryData || telemetryData.length < 2) return null;

  const points = telemetryData
    .map((d) => {
      const speedKmh = getValidNumber(d.speed);
      const gpsSpeed = getValidNumber(d.gps_speed);
      const speedMps = gpsSpeed != null ? gpsSpeed : (speedKmh != null ? speedKmh / 3.6 : null);
      // Prefer raw displacement when present so max travel comes from data (e.g. Jack’s 189.3 mm), not assumed 200/210
      const frontMm = looksLikeRawDisp(d.fork)
        ? rawDispToMm(d.fork, DEFAULT_MAX_TRAVEL_FRONT_MM)
        : toTravelMm(d.fork_normalized ?? d.fork, DEFAULT_MAX_TRAVEL_FRONT_MM);
      const rearMm = looksLikeRawDisp(d.shock)
        ? rawDispToMm(d.shock, DEFAULT_MAX_TRAVEL_REAR_MM)
        : toTravelMm(d.shock_normalized ?? d.shock, DEFAULT_MAX_TRAVEL_REAR_MM);
      return {
        time: getValidNumber(d.time),
        frontMm,
        rearMm,
        accelZ: getValidNumber(d.accel_z),
        speedMps,
      };
    })
    .filter((p) => p.time !== null)
    .sort((a, b) => a.time - b.time);

  if (points.length < 2) return null;

  const frontRaw = points.map((p) => p.frontMm);
  const rearRaw = points.map((p) => p.rearMm);
  const frontFiltered = movingAverage(frontRaw);
  const rearFiltered = movingAverage(rearRaw);

  const times = points.map((p) => p.time);
  const frontVel = [];
  const rearVel = [];
  for (let i = 0; i < points.length; i++) {
    const dt = i > 0 ? times[i] - times[i - 1] : 0;
    if (dt > 0 && dt < 1) {
      const f0 = frontFiltered[i - 1], f1 = frontFiltered[i];
      const r0 = rearFiltered[i - 1], r1 = rearFiltered[i];
      frontVel.push(f0 != null && f1 != null ? (f1 - f0) / dt : null);
      rearVel.push(r0 != null && r1 != null ? (r1 - r0) / dt : null);
    } else {
      frontVel.push(null);
      rearVel.push(null);
    }
  }

  const frontRange = getFiniteMinMax(frontRaw);
  const rearRange = getFiniteMinMax(rearRaw);
  const maxFrontMm = frontRange ? frontRange.max : DEFAULT_MAX_TRAVEL_FRONT_MM;
  const maxRearMm = rearRange ? rearRange.max : DEFAULT_MAX_TRAVEL_REAR_MM;
  const minFrontMm = frontRange ? frontRange.min : 0;
  const minRearMm = rearRange ? rearRange.min : 0;
  const rangeFront = maxFrontMm - minFrontMm || DEFAULT_MAX_TRAVEL_FRONT_MM;
  const rangeRear = maxRearMm - minRearMm || DEFAULT_MAX_TRAVEL_REAR_MM;

  return {
    times,
    frontMm: frontFiltered,
    rearMm: rearFiltered,
    frontVel,
    rearVel,
    accelZ: points.map((p) => p.accelZ),
    speedMps: points.map((p) => p.speedMps),
    maxFrontMm,
    maxRearMm,
    minFrontMm,
    minRearMm,
    rangeFront,
    rangeRear,
    frontPercent: frontFiltered.map((v) => (v != null ? ((v - minFrontMm) / rangeFront) * 100 : null)),
    rearPercent: rearFiltered.map((v) => (v != null ? ((v - minRearMm) / rangeRear) * 100 : null)),
  };
}

/** Detect all suspension events (list of { type, time_s, endTime_s?, value?, side }). */
export function detectSuspensionEvents(series) {
  if (!series || !series.times.length) return [];
  const events = [];
  const { times, frontPercent, rearPercent, frontVel, rearVel, accelZ } = series;
  const dtMs = times.length > 1 ? (times[1] - times[0]) * 1000 : 0;

  const add = (type, time_s, opts = {}) => events.push({ type, time_s, ...opts });

  for (let i = 0; i < times.length; i++) {
    const t = times[i];
    const fp = frontPercent[i]; const rp = rearPercent[i];
    const fv = frontVel[i] != null ? frontVel[i] : 0;
    const rv = rearVel[i] != null ? rearVel[i] : 0;
    const az = accelZ[i] != null ? accelZ[i] : 0;

    const prevFp = i > 0 ? frontPercent[i - 1] : null;
    const prevRp = i > 0 ? rearPercent[i - 1] : null;
    if (fp != null && fp > BOTTOM_OUT_PERCENT && (prevFp == null || prevFp <= BOTTOM_OUT_PERCENT)) {
      let dur = 0;
      let j = i;
      while (j < times.length && frontPercent[j] != null && frontPercent[j] > BOTTOM_OUT_PERCENT) {
        dur += (times[Math.min(j + 1, times.length - 1)] - times[j]) * 1000;
        j++;
      }
      if (dur >= BOTTOM_OUT_MIN_MS) add('bottom_out_front', t, { value: fp, side: 'front' });
    }
    if (rp != null && rp > BOTTOM_OUT_PERCENT && (prevRp == null || prevRp <= BOTTOM_OUT_PERCENT)) {
      let dur = 0;
      let j = i;
      while (j < times.length && rearPercent[j] != null && rearPercent[j] > BOTTOM_OUT_PERCENT) {
        dur += (times[Math.min(j + 1, times.length - 1)] - times[j]) * 1000;
        j++;
      }
      if (dur >= BOTTOM_OUT_MIN_MS) add('bottom_out_rear', t, { value: rp, side: 'rear' });
    }

    const prevFv = i > 0 ? frontVel[i - 1] : null;
    const prevRv = i > 0 ? rearVel[i - 1] : null;
    if (fv > HIGH_SPEED_VELOCITY_MM_S && (prevFv == null || prevFv <= HIGH_SPEED_VELOCITY_MM_S)) add('high_speed_compression_front', t, { value: fv, side: 'front' });
    if (rv > HIGH_SPEED_VELOCITY_MM_S && (prevRv == null || prevRv <= HIGH_SPEED_VELOCITY_MM_S)) add('high_speed_compression_rear', t, { value: rv, side: 'rear' });
    if (Math.abs(az) > IMPACT_ACCEL_G) add('impact', t, { value: az });

    if (fv > HARSH_VELOCITY_MM_S && Math.abs(az) > HARSH_ACCEL_G) add('harsh_compression_front', t, { value: fv, side: 'front' });
    if (rv > HARSH_VELOCITY_MM_S && Math.abs(az) > HARSH_ACCEL_G) add('harsh_compression_rear', t, { value: rv, side: 'rear' });
    if (fv < REBOUND_SPIKE_MM_S && (prevFv == null || prevFv >= REBOUND_SPIKE_MM_S)) add('rebound_spike_front', t, { value: fv, side: 'front' });
    if (rv < REBOUND_SPIKE_MM_S && (prevRv == null || prevRv >= REBOUND_SPIKE_MM_S)) add('rebound_spike_rear', t, { value: rv, side: 'rear' });

    if (fp != null && fp < TOP_OUT_PERCENT && fv < -100) add('top_out_front', t, { value: fp, side: 'front' });
    if (rp != null && rp < TOP_OUT_PERCENT && rv < -100) add('top_out_rear', t, { value: rp, side: 'rear' });

    if (az < -G_OUT_ACCEL_G) {
      let foundRebound = false;
      for (let k = i + 1; k < Math.min(i + 1 + Math.ceil(0.1 / (dtMs / 1000)), times.length); k++) {
        if (frontVel[k] != null && frontVel[k] < -200 || rearVel[k] != null && rearVel[k] < -200) {
          foundRebound = true;
          break;
        }
      }
      if (foundRebound) add('g_out', t, { value: az });
    }
  }

  for (let i = 1; i < times.length; i++) {
    const f0 = frontVel[i - 1]; const f1 = frontVel[i];
    const r0 = rearVel[i - 1]; const r1 = rearVel[i];
    if (f0 != null && f1 != null && f0 < 0 && f1 >= 0) add('compression_start_front', times[i], { side: 'front' });
    if (f0 != null && f1 != null && f0 >= 0 && f1 < 0) add('rebound_start_front', times[i], { side: 'front' });
    if (r0 != null && r1 != null && r0 < 0 && r1 >= 0) add('compression_start_rear', times[i], { side: 'rear' });
    if (r0 != null && r1 != null && r0 >= 0 && r1 < 0) add('rebound_start_rear', times[i], { side: 'rear' });
  }

  for (let i = 0; i < times.length; i++) {
    const windowSamples = Math.ceil(CHATTER_WINDOW_MS / (dtMs || 1));
    let frontCross = 0; let rearCross = 0;
    for (let k = Math.max(0, i - windowSamples); k < Math.min(times.length - 1, i + windowSamples); k++) {
      const f0 = frontVel[k]; const f1 = frontVel[k + 1];
      const r0 = rearVel[k]; const r1 = rearVel[k + 1];
      if (f0 != null && f1 != null && ((f0 >= 0 && f1 < 0) || (f0 < 0 && f1 >= 0))) frontCross++;
      if (r0 != null && r1 != null && ((r0 >= 0 && r1 < 0) || (r0 < 0 && r1 >= 0))) rearCross++;
    }
    if (frontCross >= CHATTER_ZERO_CROSSINGS) add('chatter_front', times[i], { side: 'front' });
    if (rearCross >= CHATTER_ZERO_CROSSINGS) add('chatter_rear', times[i], { side: 'rear' });
  }

  let lastSustainedFrontEnd = -1;
  let lastSustainedRearEnd = -1;
  for (let i = 0; i < times.length; i++) {
    let j = i;
    let dur = 0;
    while (j < times.length && frontVel[j] != null && frontVel[j] > SUSTAINED_VELOCITY_MM_S) {
      dur += (times[Math.min(j + 1, times.length - 1)] - times[j]) * 1000;
      j++;
    }
    if (dur >= SUSTAINED_MIN_MS && i > lastSustainedFrontEnd) {
      add('sustained_compression_front', times[i], { endTime_s: times[j - 1], side: 'front' });
      lastSustainedFrontEnd = j - 1;
    }
    j = i; dur = 0;
    while (j < times.length && rearVel[j] != null && rearVel[j] > SUSTAINED_VELOCITY_MM_S) {
      dur += (times[Math.min(j + 1, times.length - 1)] - times[j]) * 1000;
      j++;
    }
    if (dur >= SUSTAINED_MIN_MS && i > lastSustainedRearEnd) {
      add('sustained_compression_rear', times[i], { endTime_s: times[j - 1], side: 'rear' });
      lastSustainedRearEnd = j - 1;
    }
  }

  events.sort((a, b) => a.time_s - b.time_s);
  return events;
}

const STATIC_SAG_SPEED_THRESHOLD_MPS = 0.2;
const STATIC_SAG_MIN_DURATION_S = 1;
const STATIC_SAG_FALLBACK_FIRST_S = 2;

/** Compute full suspension metrics (MVP + histograms). */
export function computeSuspensionMetrics(series, events) {
  if (!series) return null;
  const { times, frontMm, rearMm, frontVel, rearVel, accelZ, speedMps, maxFrontMm, maxRearMm, rangeFront, rangeRear } = series;

  const validFront = frontVel.filter((v) => v != null);
  const validRear = rearVel.filter((v) => v != null);
  const validAz = accelZ.filter((v) => v != null);

  const peakCompFront = validFront.length ? getFiniteMax(validFront) : null;
  const peakCompRear = validRear.length ? getFiniteMax(validRear) : null;
  const peakReboundFront = validFront.length ? getFiniteMin(validFront) : null;
  const peakReboundRear = validRear.length ? getFiniteMin(validRear) : null;

  const rms = (arr) => {
    if (!arr.length) return null;
    const sumSq = arr.reduce((s, v) => s + v * v, 0);
    return Math.sqrt(sumSq / arr.length);
  };
  const rmsFrontVel = rms(validFront);
  const rmsRearVel = rms(validRear);
  const rmsAccelZ = rms(validAz.map((a) => Math.abs(a)));
  const peakAccelZ = validAz.length ? getFiniteMax(validAz.map((a) => Math.abs(a))) : null;

  const bottomOutFront = events ? events.filter((e) => e.type === 'bottom_out_front').length : 0;
  const bottomOutRear = events ? events.filter((e) => e.type === 'bottom_out_rear').length : 0;
  const topOutFront = events ? events.filter((e) => e.type === 'top_out_front').length : 0;
  const topOutRear = events ? events.filter((e) => e.type === 'top_out_rear').length : 0;
  const highSpeedFront = events ? events.filter((e) => e.type === 'high_speed_compression_front').length : 0;
  const highSpeedRear = events ? events.filter((e) => e.type === 'high_speed_compression_rear').length : 0;
  const impactCount = events ? events.filter((e) => e.type === 'impact').length : 0;

  const meanFront = validFront.length ? validFront.reduce((a, b) => a + b, 0) / validFront.length : null;
  const meanRear = validRear.length ? validRear.reduce((a, b) => a + b, 0) / validRear.length : null;
  const frontTravelMean = frontMm.filter((v) => v != null).reduce((a, b) => a + b, 0) / (frontMm.filter((v) => v != null).length || 1);
  const rearTravelMean = rearMm.filter((v) => v != null).reduce((a, b) => a + b, 0) / (rearMm.filter((v) => v != null).length || 1);
  const balanceRatio = rearTravelMean > 0 ? frontTravelMean / rearTravelMean : null;

  const highSpeedTotal = highSpeedFront + highSpeedRear;
  const harshnessIndex = rmsAccelZ != null ? rmsAccelZ * highSpeedTotal : null;

  let staticSagFront = null;
  let staticSagRear = null;
  let dynamicSagFront = null;
  let dynamicSagRear = null;
  const t0 = times[0];
  const firstEndIdx = times.findIndex((t) => t >= t0 + STATIC_SAG_FALLBACK_FIRST_S);
  const endIdx = firstEndIdx <= 0 ? Math.min(Math.ceil(STATIC_SAG_FALLBACK_FIRST_S / (times[1] - times[0] || 0.002)), times.length - 1) : firstEndIdx;
  const hasSpeed = speedMps && speedMps.some((v) => v != null);
  if (hasSpeed) {
    let staticStart = null;
    let staticEnd = null;
    for (let i = 0; i < times.length; i++) {
      const v = speedMps[i] != null ? speedMps[i] : 999;
      if (v < STATIC_SAG_SPEED_THRESHOLD_MPS) {
        if (staticStart == null) staticStart = i;
        staticEnd = i;
      } else if (staticStart != null && staticEnd != null) {
        const dur = times[staticEnd] - times[staticStart];
        if (dur >= STATIC_SAG_MIN_DURATION_S) break;
        staticStart = null;
        staticEnd = null;
      }
    }
    const hasStationaryWindow = staticStart != null && staticEnd != null && times[staticEnd] - times[staticStart] >= STATIC_SAG_MIN_DURATION_S;
    if (hasStationaryWindow) {
      let sumF = 0, sumR = 0, nF = 0, nR = 0;
      for (let i = staticStart; i <= staticEnd; i++) {
        if (frontMm[i] != null) { sumF += frontMm[i]; nF++; }
        if (rearMm[i] != null) { sumR += rearMm[i]; nR++; }
      }
      staticSagFront = nF ? sumF / nF : null;
      staticSagRear = nR ? sumR / nR : null;
    }
    if (!hasStationaryWindow) {
      // Fallback: use first N seconds for static (match Jack / “rider standing at start”)
      let sumF = 0, sumR = 0, nF = 0, nR = 0;
      for (let i = 0; i <= endIdx && i < times.length; i++) {
        if (frontMm[i] != null) { sumF += frontMm[i]; nF++; }
        if (rearMm[i] != null) { sumR += rearMm[i]; nR++; }
      }
      staticSagFront = nF ? sumF / nF : null;
      staticSagRear = nR ? sumR / nR : null;
    }
    const ridingIndices = [];
    for (let i = 0; i < times.length; i++) {
      const v = speedMps[i] != null ? speedMps[i] : 0;
      if (v >= STATIC_SAG_SPEED_THRESHOLD_MPS) ridingIndices.push(i);
    }
    if (ridingIndices.length > 0) {
      let sumF = 0, sumR = 0, nF = 0, nR = 0;
      ridingIndices.forEach((i) => {
        if (frontMm[i] != null) { sumF += frontMm[i]; nF++; }
        if (rearMm[i] != null) { sumR += rearMm[i]; nR++; }
      });
      dynamicSagFront = nF ? sumF / nF : null;
      dynamicSagRear = nR ? sumR / nR : null;
    } else {
      // No “moving” window: use rest of run after first N s as dynamic (match no-speed fallback)
      let sumF = 0, sumR = 0, nF = 0, nR = 0;
      for (let i = endIdx + 1; i < times.length; i++) {
        if (frontMm[i] != null) { sumF += frontMm[i]; nF++; }
        if (rearMm[i] != null) { sumR += rearMm[i]; nR++; }
      }
      dynamicSagFront = nF ? sumF / nF : null;
      dynamicSagRear = nR ? sumR / nR : null;
    }
  } else {
    let sumF = 0, sumR = 0, nF = 0, nR = 0;
    for (let i = 0; i <= endIdx && i < times.length; i++) {
      if (frontMm[i] != null) { sumF += frontMm[i]; nF++; }
      if (rearMm[i] != null) { sumR += rearMm[i]; nR++; }
    }
    if (nF > 0) staticSagFront = sumF / nF;
    if (nR > 0) staticSagRear = sumR / nR;
    let sumFd = 0, sumRd = 0, nFd = 0, nRd = 0;
    for (let i = endIdx + 1; i < times.length; i++) {
      if (frontMm[i] != null) { sumFd += frontMm[i]; nFd++; }
      if (rearMm[i] != null) { sumRd += rearMm[i]; nRd++; }
    }
    if (nFd > 0) dynamicSagFront = sumFd / nFd;
    if (nRd > 0) dynamicSagRear = sumRd / nRd;
  }

  const rmsTravelFront = rms(frontMm.filter((v) => v != null));
  const suspensionEfficiency = (rmsAccelZ != null && rmsAccelZ > 0 && rmsTravelFront != null)
    ? rmsTravelFront / rmsAccelZ
    : null;

  const travelHistogram = (arr, minV, rangeV) => {
    const bins = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
    let total = 0;
    arr.forEach((v) => {
      if (v == null) return;
      const pct = rangeV ? ((v - minV) / rangeV) * 100 : 0;
      const bin = Math.min(9, Math.floor(pct / 10));
      bins[bin]++;
      total++;
    });
    return total ? bins.map((b) => (b / total) * 100) : bins;
  };
  const minFront = getFiniteMin(frontMm) ?? 0;
  const minRear = getFiniteMin(rearMm) ?? 0;
  const histFront = travelHistogram(frontMm, minFront, rangeFront);
  const histRear = travelHistogram(rearMm, minRear, rangeRear);

  const velocityHistogram = (velArr) => {
    const bins = [0, 0, 0, 0];
    velArr.forEach((v) => {
      if (v == null) return;
      const u = Math.abs(v);
      if (u < 50) bins[0]++;
      else if (u < 200) bins[1]++;
      else if (u < 500) bins[2]++;
      else bins[3]++;
    });
    const total = bins.reduce((a, b) => a + b, 0);
    return total ? bins.map((b) => (b / total) * 100) : bins;
  };

  const frontStrokes = segmentStrokes(frontVel);
  const rearStrokes = segmentStrokes(rearVel);
  const compFront = strokeStats(frontStrokes.compression);
  const rebFront = strokeStats(frontStrokes.rebound);
  const compRear = strokeStats(rearStrokes.compression);
  const rebRear = strokeStats(rearStrokes.rebound);

  let totalMovementFrontMm = 0;
  let totalMovementRearMm = 0;
  let totalCompressionFrontMm = 0;
  let totalReboundFrontMm = 0;
  let totalCompressionRearMm = 0;
  let totalReboundRearMm = 0;
  for (let i = 1; i < times.length; i++) {
    const dt = times[i] - times[i - 1];
    if (dt <= 0 || dt >= 1) continue;
    const fv = frontVel[i];
    const rv = rearVel[i];
    if (fv != null) {
      totalMovementFrontMm += Math.abs(fv) * dt;
      if (fv > 0) totalCompressionFrontMm += fv * dt;
      else totalReboundFrontMm += Math.abs(fv) * dt;
    }
    if (rv != null) {
      totalMovementRearMm += Math.abs(rv) * dt;
      if (rv > 0) totalCompressionRearMm += rv * dt;
      else totalReboundRearMm += Math.abs(rv) * dt;
    }
  }
  const totalMovementFrontM = totalMovementFrontMm / 1000;
  const totalMovementRearM = totalMovementRearMm / 1000;

  const validFrontMm = frontMm.filter((v) => v != null);
  const validRearMm = rearMm.filter((v) => v != null);
  const position95Front = validFrontMm.length ? percentile([...validFrontMm].sort((a, b) => a - b), 95) : null;
  const position95Rear = validRearMm.length ? percentile([...validRearMm].sort((a, b) => a - b), 95) : null;
  const meanFrontMm = validFrontMm.length ? validFrontMm.reduce((a, b) => a + b, 0) / validFrontMm.length : 0;
  const meanRearMm = validRearMm.length ? validRearMm.reduce((a, b) => a + b, 0) / validRearMm.length : 0;
  const stdFront = validFrontMm.length && validFrontMm.length > 1
    ? Math.sqrt(validFrontMm.reduce((s, v) => s + (v - meanFrontMm) ** 2, 0) / (validFrontMm.length - 1))
    : null;
  const stdRear = validRearMm.length && validRearMm.length > 1
    ? Math.sqrt(validRearMm.reduce((s, v) => s + (v - meanRearMm) ** 2, 0) / (validRearMm.length - 1))
    : null;

  const avgStrokeCompFront = compFront.count > 0 ? totalCompressionFrontMm / compFront.count : null;
  const avgStrokeRebFront = rebFront.count > 0 ? totalReboundFrontMm / rebFront.count : null;
  const avgStrokeCompRear = compRear.count > 0 ? totalCompressionRearMm / compRear.count : null;
  const avgStrokeRebRear = rebRear.count > 0 ? totalReboundRearMm / rebRear.count : null;

  return {
    max_front_travel: maxFrontMm,
    max_rear_travel: maxRearMm,
    min_front_travel: getFiniteMin(frontMm),
    min_rear_travel: getFiniteMin(rearMm),
    static_sag_front: staticSagFront,
    static_sag_rear: staticSagRear,
    dynamic_sag_front: dynamicSagFront,
    dynamic_sag_rear: dynamicSagRear,
    bottom_out_front: bottomOutFront,
    bottom_out_rear: bottomOutRear,
    top_out_front: topOutFront,
    top_out_rear: topOutRear,
    position_95th_front_mm: position95Front,
    position_95th_rear_mm: position95Rear,
    position_std_front_mm: stdFront,
    position_std_rear_mm: stdRear,
    total_movement_front_m: totalMovementFrontM,
    total_movement_rear_m: totalMovementRearM,
    compression_strokes_front_avg_stroke_mm: avgStrokeCompFront,
    compression_strokes_rear_avg_stroke_mm: avgStrokeCompRear,
    rebound_strokes_front_avg_stroke_mm: avgStrokeRebFront,
    rebound_strokes_rear_avg_stroke_mm: avgStrokeRebRear,
    compression_strokes_front_avg_max_vel: compFront.avg_max_vel,
    compression_strokes_rear_avg_max_vel: compRear.avg_max_vel,
    rebound_strokes_front_avg_max_vel: rebFront.avg_max_vel,
    rebound_strokes_rear_avg_max_vel: rebRear.avg_max_vel,
    peak_front_compression: peakCompFront,
    peak_rear_compression: peakCompRear,
    peak_front_rebound: peakReboundFront,
    peak_rear_rebound: peakReboundRear,
    rms_front_velocity: rmsFrontVel,
    rms_rear_velocity: rmsRearVel,
    rms_accel_z: rmsAccelZ,
    peak_accel_z: peakAccelZ,
    high_speed_compression_front: highSpeedFront,
    high_speed_compression_rear: highSpeedRear,
    impact_count: impactCount,
    front_rear_balance_ratio: balanceRatio,
    harshness_index: harshnessIndex,
    suspension_efficiency: suspensionEfficiency,
    travel_histogram_front: histFront,
    travel_histogram_rear: histRear,
    velocity_histogram_front: velocityHistogram(validFront),
    velocity_histogram_rear: velocityHistogram(validRear),
    compression_strokes_front_count: compFront.count,
    compression_strokes_front_max_speed: compFront.max_speed,
    compression_strokes_front_avg_speed: compFront.avg_speed,
    compression_strokes_front_avg_max_vel: compFront.avg_max_vel,
    compression_strokes_front_p95_speed: compFront.p95_speed,
    compression_strokes_rear_count: compRear.count,
    compression_strokes_rear_max_speed: compRear.max_speed,
    compression_strokes_rear_avg_speed: compRear.avg_speed,
    compression_strokes_rear_avg_max_vel: compRear.avg_max_vel,
    compression_strokes_rear_p95_speed: compRear.p95_speed,
    rebound_strokes_front_count: rebFront.count,
    rebound_strokes_front_max_speed: rebFront.max_speed,
    rebound_strokes_front_avg_speed: rebFront.avg_speed,
    rebound_strokes_front_avg_max_vel: rebFront.avg_max_vel,
    rebound_strokes_front_p95_speed: rebFront.p95_speed,
    rebound_strokes_rear_count: rebRear.count,
    rebound_strokes_rear_max_speed: rebRear.max_speed,
    rebound_strokes_rear_avg_speed: rebRear.avg_speed,
    rebound_strokes_rear_avg_max_vel: rebRear.avg_max_vel,
    rebound_strokes_rear_p95_speed: rebRear.p95_speed,
  };
}

/** Significance 0=ignore for "big events", 1=minor, 2=moderate, 3=major. Used to filter and rank. */
const EVENT_SIGNIFICANCE = {
  bottom_out_front: 3, bottom_out_rear: 3,
  impact: 3, g_out: 3,
  harsh_compression_front: 2, harsh_compression_rear: 2,
  high_speed_compression_front: 2, high_speed_compression_rear: 2,
  sustained_compression_front: 2, sustained_compression_rear: 2,
  rebound_spike_front: 2, rebound_spike_rear: 2,
  top_out_front: 1, top_out_rear: 1,
  compression_start_front: 0, rebound_start_front: 0,
  compression_start_rear: 0, rebound_start_rear: 0,
  chatter_front: 0, chatter_rear: 0,
};

/** Short combined labels for F+R (e.g. "Bottom-out (F+R)"). */
const EVENT_LABEL_COMBINED = {
  bottom_out: 'Bottom-out (F+R)',
  high_speed_compression: 'High-speed compression (F+R)',
  impact: 'Impact',
  harsh_compression: 'Harsh compression (F+R)',
  sustained_compression: 'Sustained compression (F+R)',
  rebound_spike: 'Rebound spike (F+R)',
  top_out: 'Top-out (F+R)',
  g_out: 'G-out / landing',
};

/** Get median of numeric array (ignores null/NaN). */
function median(arr) {
  const valid = arr.filter((v) => v != null && Number.isFinite(v));
  if (!valid.length) return 0;
  const sorted = [...valid].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

/** Get value at percentile (0-100) from a sorted array. */
function percentile(sortedArr, p) {
  if (!sortedArr.length) return 0;
  const idx = (p / 100) * (sortedArr.length - 1);
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return sortedArr[lo];
  return sortedArr[lo] + (idx - lo) * (sortedArr[hi] - sortedArr[lo]);
}

/**
 * Segment velocity array into compression (v > 0) and rebound (v < 0) strokes.
 * Returns { compression: [{ velocities: number[] }], rebound: [{ velocities: number[] }] }.
 */
function segmentStrokes(velArr) {
  const compression = [];
  const rebound = [];
  let compCur = [];
  let rebCur = [];
  for (let i = 0; i < velArr.length; i++) {
    const v = velArr[i];
    if (v == null || !Number.isFinite(v)) {
      if (compCur.length) { compression.push({ velocities: compCur }); compCur = []; }
      if (rebCur.length) { rebound.push({ velocities: rebCur }); rebCur = []; }
      continue;
    }
    if (v > 0) {
      if (rebCur.length) { rebound.push({ velocities: rebCur }); rebCur = []; }
      compCur.push(v);
    } else if (v < 0) {
      if (compCur.length) { compression.push({ velocities: compCur }); compCur = []; }
      rebCur.push(Math.abs(v)); // store magnitude for rebound "speed"
    } else {
      if (compCur.length) { compression.push({ velocities: compCur }); compCur = []; }
      if (rebCur.length) { rebound.push({ velocities: rebCur }); rebCur = []; }
    }
  }
  if (compCur.length) compression.push({ velocities: compCur });
  if (rebCur.length) rebound.push({ velocities: rebCur });
  return { compression, rebound };
}

/**
 * Compute stroke stats: count, max speed, average speed, 95th percentile speed,
 * avg_max_vel (average of per-stroke maximum velocity, to match reference "Avg Max Vel").
 */
function strokeStats(strokes) {
  const allSpeeds = strokes.flatMap((s) => s.velocities);
  const count = strokes.length;
  if (!allSpeeds.length) {
    return { count: 0, max_speed: null, avg_speed: null, p95_speed: null, avg_max_vel: null };
  }
  const max_speed = getFiniteMax(allSpeeds);
  const avg_speed = allSpeeds.reduce((a, b) => a + b, 0) / allSpeeds.length;
  const sorted = [...allSpeeds].sort((a, b) => a - b);
  const p95_speed = percentile(sorted, 95);
  const perStrokeMax = strokes.map((s) => (s.velocities.length ? (getFiniteMax(s.velocities) ?? 0) : 0));
  const avg_max_vel = perStrokeMax.length ? perStrokeMax.reduce((a, b) => a + b, 0) / perStrokeMax.length : null;
  return { count, max_speed, avg_speed, p95_speed, avg_max_vel };
}

/**
 * Compute per-sample scores over the full run: deviation from baseline (sag) + activity.
 * Baseline = median travel (sag level). Deviation = amplitude from sag + |accel|.
 * Activity = |velocity| so "lots of small changes" (chatter) also scores high.
 * Returns { amplitudeScores, activityScores, combinedScores } (same length as times).
 */
function computeDeviationAndActivityScores(series) {
  if (!series || !series.times.length) return { amplitudeScores: [], activityScores: [], combinedScores: [] };
  const { frontMm, rearMm, frontVel, rearVel, accelZ, rangeFront, rangeRear } = series;
  const n = series.times.length;
  const sagFront = median(frontMm);
  const sagRear = median(rearMm);
  const scaleFront = rangeFront && rangeFront > 0 ? rangeFront : 1;
  const scaleRear = rangeRear && rangeRear > 0 ? rangeRear : 1;

  const amplitudeScores = [];
  const activityScores = [];
  for (let i = 0; i < n; i++) {
    const devFront = frontMm[i] != null ? Math.abs(frontMm[i] - sagFront) / scaleFront : 0;
    const devRear = rearMm[i] != null ? Math.abs(rearMm[i] - sagRear) / scaleRear : 0;
    const acc = accelZ[i] != null ? Math.abs(accelZ[i]) : 0;
    amplitudeScores.push(devFront + devRear + acc * 0.1);
    const fv = frontVel[i] != null ? Math.abs(frontVel[i]) : 0;
    const rv = rearVel[i] != null ? Math.abs(rearVel[i]) : 0;
    activityScores.push(fv / 1000 + rv / 1000);
  }

  const aSorted = [...amplitudeScores].sort((a, b) => a - b);
  const vSorted = [...activityScores].sort((a, b) => a - b);
  const aMax = percentile(aSorted, 99) || 1;
  const vMax = percentile(vSorted, 99) || 1;
  const combinedScores = amplitudeScores.map((a, i) => (a / aMax) + (activityScores[i] / vMax));
  return { amplitudeScores, activityScores, combinedScores };
}

/**
 * Detect events over the full run: segments with highest deviation/activity.
 * desiredEventCount = number of events to return (slider value). Algorithm finds candidate
 * segments (above-idle over full duration), ranks by strength (amplitude + activity),
 * returns the top N so you get exactly the count you asked for.
 * Events = either one big deviation (amplitude) or a series of small changes (activity).
 * @param {Object} series - from buildSuspensionSeries
 * @param {{ desiredEventCount?: number, mergeWindowSeconds?: number }} options
 */
export function detectEventsByDeviation(series, options = {}) {
  const desiredEventCount = Math.max(1, Math.min(100, options.desiredEventCount ?? 10));
  const mergeWindowSeconds = options.mergeWindowSeconds ?? 1.5;

  const { combinedScores, amplitudeScores, activityScores } = computeDeviationAndActivityScores(series);
  if (!combinedScores.length) return [];

  const times = series.times;
  const sortedScores = [...combinedScores].sort((a, b) => a - b);
  const lowThreshold = percentile(sortedScores, 25);
  const segments = [];
  let segStart = null;
  let segEnd = null;
  for (let i = 0; i < times.length; i++) {
    if (combinedScores[i] >= lowThreshold) {
      if (segStart == null) segStart = i;
      segEnd = i;
    } else {
      if (segStart != null && segEnd != null) {
        segments.push({
          startIdx: segStart,
          endIdx: segEnd,
          start_s: times[segStart],
          end_s: times[segEnd],
        });
        segStart = null;
        segEnd = null;
      }
    }
  }
  if (segStart != null && segEnd != null) {
    segments.push({
      startIdx: segStart,
      endIdx: segEnd,
      start_s: times[segStart],
      end_s: times[segEnd],
    });
  }

  if (!segments.length) return [];

  const merged = [];
  for (let i = 0; i < segments.length; i++) {
    let start_s = segments[i].start_s;
    let end_s = segments[i].end_s;
    let startIdx = segments[i].startIdx;
    let endIdx = segments[i].endIdx;
    while (i + 1 < segments.length && segments[i + 1].start_s <= end_s + mergeWindowSeconds) {
      i++;
      end_s = Math.max(end_s, segments[i].end_s);
      endIdx = Math.max(endIdx, segments[i].endIdx);
    }
    merged.push({ start_s, end_s, startIdx, endIdx });
  }

  const strength = (seg) => {
    let amp = 0;
    let act = 0;
    for (let j = seg.startIdx; j <= seg.endIdx; j++) {
      if (amplitudeScores[j] > amp) amp = amplitudeScores[j];
      act += activityScores[j];
    }
    const len = seg.endIdx - seg.startIdx + 1;
    return amp + (len > 0 ? act / len : 0);
  };
  merged.sort((a, b) => strength(b) - strength(a));
  const topN = merged.slice(0, desiredEventCount);
  topN.sort((a, b) => a.start_s - b.start_s);
  return topN.map((ev, idx) => ({
    start_s: ev.start_s,
    end_s: ev.end_s,
    duration_s: Math.max(0, ev.end_s - ev.start_s),
    label: `Impact ${idx + 1}`,
  }));
}

/**
 * Consolidate raw events into at most maxEvents "big" events: group by time window, combine F+R.
 * (Legacy rule-based detection; prefer detectEventsByDeviation for deviation-based behaviour.)
 */
export function consolidateSuspensionEvents(rawEvents, options = {}) {
  const maxEvents = options.maxEvents ?? 20;
  const mergeWindowSeconds = options.mergeWindowSeconds ?? 1.5;
  const minSignificance = options.minSignificance ?? 2;

  const significant = rawEvents.filter((e) => (EVENT_SIGNIFICANCE[e.type] ?? 0) >= minSignificance);
  if (!significant.length) return [];

  const withEnd = significant.map((e) => ({
    ...e,
    end_s: e.endTime_s ?? e.time_s,
    severity: EVENT_SIGNIFICANCE[e.type] ?? 0,
  }));

  const merged = [];
  const used = new Set();
  for (let i = 0; i < withEnd.length; i++) {
    if (used.has(i)) continue;
    const seed = withEnd[i];
    let start_s = seed.time_s;
    let end_s = seed.end_s;
    const types = new Set();
    types.add(seed.type.replace(/_front$|_rear$/, ''));
    for (let j = i + 1; j < withEnd.length; j++) {
      if (used.has(j)) continue;
      const o = withEnd[j];
      if (o.time_s <= end_s + mergeWindowSeconds && o.end_s >= start_s - mergeWindowSeconds) {
        used.add(j);
        start_s = Math.min(start_s, o.time_s);
        end_s = Math.max(end_s, o.end_s);
        types.add(o.type.replace(/_front$|_rear$/, ''));
      }
    }
    used.add(i);
    const duration_s = Math.max(0, end_s - start_s);
    merged.push({ start_s, end_s, duration_s });
  }

  merged.sort((a, b) => a.start_s - b.start_s);
  return merged.slice(0, maxEvents).map((ev, idx) => ({
    ...ev,
    label: `Impact ${idx + 1}`,
  }));
}

export const DEFAULT_MAX_SUSPENSION_EVENTS = 20;

/**
 * Linear regression: returns { slope, intercept } for y = slope*x + intercept.
 */
function linearRegression(points) {
  const n = points.length;
  if (n < 2) return { slope: 0, intercept: 0 };
  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumX2 = 0;
  for (const p of points) {
    sumX += p.x;
    sumY += p.y;
    sumXY += p.x * p.y;
    sumX2 += p.x * p.x;
  }
  const denom = n * sumX2 - sumX * sumX;
  const slope = denom !== 0 ? (n * sumXY - sumX * sumY) / denom : 0;
  const intercept = (sumY - slope * sumX) / n;
  return { slope, intercept };
}

/**
 * Compute velocity balance data: velocity (mm/s) vs travel % for compression and rebound.
 * Returns scatter points and regression lines for front and rear, used for velocity balance charts.
 * @param {Object} series - from buildSuspensionSeries
 * @returns {{ compression: { front: { points, slope, intercept }, rear: { points, slope, intercept } }, rebound: { front: { points, slope, intercept }, rear: { points, slope, intercept } } }}
 */
export function computeVelocityBalanceData(series) {
  if (!series || !series.times?.length) {
    return { compression: { front: null, rear: null }, rebound: { front: null, rear: null } };
  }
  const { frontPercent, rearPercent, frontVel, rearVel } = series;

  const build = (pctArr, velArr, filterVel) => {
    const points = [];
    for (let i = 0; i < pctArr.length; i++) {
      const x = pctArr[i];
      const y = velArr[i];
      if (x != null && Number.isFinite(x) && y != null && Number.isFinite(y) && filterVel(y)) {
        points.push({ x, y });
      }
    }
    const reg = linearRegression(points);
    return { points, slope: reg.slope, intercept: reg.intercept };
  };

  const compressionFront = build(frontPercent, frontVel, (v) => v > 0);
  const compressionRear = build(rearPercent, rearVel, (v) => v > 0);
  const reboundFront = build(frontPercent, frontVel, (v) => v < 0);
  const reboundRear = build(rearPercent, rearVel, (v) => v < 0);

  return {
    compression: { front: compressionFront, rear: compressionRear },
    rebound: { front: reboundFront, rear: reboundRear },
  };
}

/** Human-readable event type labels. */
export const SUSPENSION_EVENT_LABELS = {
  bottom_out_front: 'Bottom-out (front)',
  bottom_out_rear: 'Bottom-out (rear)',
  high_speed_compression_front: 'High-speed compression (front)',
  high_speed_compression_rear: 'High-speed compression (rear)',
  impact: 'Impact (vertical)',
  compression_start_front: 'Compression start (front)',
  rebound_start_front: 'Rebound start (front)',
  compression_start_rear: 'Compression start (rear)',
  rebound_start_rear: 'Rebound start (rear)',
  top_out_front: 'Top-out (front)',
  top_out_rear: 'Top-out (rear)',
  harsh_compression_front: 'Harsh compression (front)',
  harsh_compression_rear: 'Harsh compression (rear)',
  sustained_compression_front: 'Sustained compression (front)',
  sustained_compression_rear: 'Sustained compression (rear)',
  rebound_spike_front: 'Rebound spike (front)',
  rebound_spike_rear: 'Rebound spike (rear)',
  g_out: 'G-out / landing',
  chatter_front: 'Chatter (front)',
  chatter_rear: 'Chatter (rear)',
};
